// Session RC1: the COMPANY STANDARD RATE CARD, one admin-only card in Settings, Subcontractors section (no new page, no new
// menu item: the calm screen rule). It shows the card in force (Bona Fide and Labour Only side by side, the uplifts in
// words), its version history (label, effective date, how many subcontractor cards were copied from it) and "Add new
// version": the figures prefilled from the version in force, the server's preview (every line that changes, every
// Standard-card subcontractor it moves and any approved week), then the save. A version no subcontractor has a copy of can
// be deleted through the destructive two-step modal; the server's refusal is relayed word for word.
//
// Nothing here reaches a subcontractor: they read the new figures on their portal's Your rates page from the effective date.
import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError, api } from "../../lib/api";
import type { StandardCardPreview, StandardCardVersion, StandardCardView } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { formatDateLong } from "../../lib/format";
import { londonToday, rateBodyFromForm, rateFormFrom, type RateFiguresForm } from "../../lib/rateCardUi";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { ModalShell } from "../../components/ModalShell";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { RateCardTable, RateFiguresFields } from "./RateCardFigures";

export function StandardRateCardSettingsCard({ refreshKey }: { refreshKey?: number }) {
  const [view, setView] = useState<StandardCardView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<StandardCardVersion | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = useCallback(() => {
    api.standardRateCard.get().then(
      (v) => {
        setView(v);
        setError(null);
      },
      (e) => setError(friendlyApiError(e, "Could not load the standard rate card.")),
    );
  }, []);
  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const current = view?.versions.find((v) => v.id === view.currentVersionId) ?? null;

  async function confirmDelete() {
    if (!deleting) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await api.standardRateCard.remove(deleting.id);
      setNotice(`${deleting.label} deleted.`);
      setDeleting(null);
      load();
    } catch (e) {
      setDeleteError(e instanceof ApiError && e.message ? e.message : "Could not delete that version.");
    } finally {
      setDeleteBusy(false);
    }
  }

  return (
    <section className="mt-5 rounded-ele border border-ele-purple-accent bg-ele-surface p-5" aria-label="Standard rate card">
      <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="font-heading text-lg text-ele-blue">Standard rate card</h2>
          <p className="mt-1 max-w-2xl text-sm text-ele-body">
            What we pay every labour subcontractor on the standard card. A new version moves each of them from its effective date; firms on custom rates are left
            alone.
          </p>
        </div>
        {view && (
          <Button size="sm" onClick={() => setAdding(true)}>
            Add new version
          </Button>
        )}
      </div>
      {error && (
        <p role="alert" className="mb-3 rounded-ele border border-ele-danger px-3 py-2 text-sm text-ele-strong">
          {error}
        </p>
      )}
      {notice && <p className="mb-3 rounded-ele border border-ele-green px-3 py-2 text-sm text-ele-strong">{notice}</p>}
      {!view && !error ? (
        <Loader shape="inline" />
      ) : view ? (
        <>
          {current ? (
            <RateCardTable card={current} caption={`In force: ${current.label}, from ${current.effectiveFromLabel}`} />
          ) : (
            <p role="status" className="text-sm text-ele-body">No version is in force today.</p>
          )}
          <p className="mt-3 text-xs text-ele-body">
            On the standard card: {view.onStandardCard.length === 0 ? "nobody yet." : view.onStandardCard.map((s) => s.company).join(", ")}
          </p>
          <p className="mt-4 font-menu text-xs uppercase tracking-widest text-ele-blue">Versions</p>
          <ul className="mt-1 divide-y divide-ele-purple-accent/40">
            {[...view.versions].reverse().map((v) => (
              <li key={v.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                <div>
                  <span className="text-ele-strong">{v.label}</span>
                  <span className="text-ele-body"> from {formatDateLong(v.effectiveFrom)}</span>
                  {v.id === view.currentVersionId && <span className="ml-2 font-menu text-xs uppercase tracking-widest text-ele-green">In force</span>}
                  <span className="block text-xs text-ele-body">
                    Copied to {v.copies} subcontractor card{v.copies === 1 ? "" : "s"}
                    {v.createdByName ? `. Saved by ${v.createdByName}` : ""}
                  </span>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setDeleting(v)} aria-label={`Delete ${v.label}`}>
                  Delete
                </Button>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {adding && view && (
        <AddStandardVersionModal
          base={current ?? view.versions[view.versions.length - 1] ?? null}
          today={view.today || londonToday()}
          onClose={() => setAdding(false)}
          onSaved={(message) => {
            setAdding(false);
            setNotice(message);
            load();
          }}
        />
      )}

      <ConfirmDeleteModal
        open={deleting != null}
        title={deleting ? `Delete ${deleting.label}?` : "Delete"}
        message={
          deleting
            ? `${deleting.label}, from ${formatDateLong(deleting.effectiveFrom)}, will be removed permanently. It cannot be deleted while any subcontractor's card was copied from it, or while it is the only version.`
            : ""
        }
        confirmLabel="Delete this version"
        variant="destructive"
        busy={deleteBusy}
        error={deleteError}
        onConfirm={() => void confirmDelete()}
        onCancel={() => {
          setDeleting(null);
          setDeleteError(null);
        }}
      />
    </section>
  );
}

function AddStandardVersionModal({
  base,
  today,
  onClose,
  onSaved,
}: {
  base: StandardCardVersion | null;
  today: string;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const [form, setForm] = useState<RateFiguresForm>(() => rateFormFrom(base));
  const [effectiveFrom, setEffectiveFrom] = useState(today);
  const [sourceNote, setSourceNote] = useState("");
  const [preview, setPreview] = useState<StandardCardPreview | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const weeks = useMemo(() => (preview ? preview.moves.flatMap((m) => m.approvedWeeksMoved.map((w) => ({ ...w, company: m.company }))) : []), [preview]);

  function bodyOrError() {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(effectiveFrom)) return { error: "Enter the effective date." };
    if (effectiveFrom < today) return { error: "A new version starts today or later, never in the past." };
    const built = rateBodyFromForm(form);
    if ("error" in built) return built;
    return { body: { ...built.body, effectiveFrom, sourceNote: sourceNote.trim() || null } };
  }

  async function review() {
    const b = bodyOrError();
    if ("error" in b) {
      setError(b.error);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.standardRateCard.create({ ...b.body, preview: true });
      if ("preview" in res) {
        setPreview(res);
        setAcknowledged(false);
      }
    } catch (e) {
      setError(e instanceof ApiError && e.message ? e.message : "Could not review the new version.");
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    const b = bodyOrError();
    if ("error" in b || !preview) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.standardRateCard.create({ ...b.body, ...(weeks.length > 0 && acknowledged ? { affectsApprovedWeeks: true } : {}) });
      if (!("preview" in res)) {
        const moved = res.moved ?? [];
        onSaved(`${res.version.label} added, in force from ${res.version.effectiveFromLabel}.${moved.length ? ` Moved: ${moved.join(", ")}.` : ""}`);
      }
    } catch (e) {
      // approved_weeks_affected or subcontractor_date_taken: the server's sentence, and review again.
      setError(e instanceof ApiError && e.message ? e.message : "Could not save the new version.");
      setPreview(null);
    } finally {
      setBusy(false);
    }
  }

  const change = (next: RateFiguresForm) => {
    setForm(next);
    setPreview(null);
  };
  const needsAck = weeks.length > 0;
  const canSave = preview != null && preview.blocked.length === 0 && (!needsAck || acknowledged);

  return (
    <ModalShell ariaLabel="Add a standard card version" onClose={() => !busy && onClose()} width="max-w-[720px]">
      <h2 className="mb-2 font-heading text-2xl tracking-wide text-ele-strong">Add new version</h2>
      <p className="mb-3 text-sm text-ele-body">Prefilled from the version in force. Pounds and pence per line; leave a line blank for not agreed. Review before saving.</p>
      <div className="mb-3 grid gap-3 sm:grid-cols-2">
        <label className="text-sm text-ele-body">
          Effective from
          <input
            type="date"
            aria-label="Effective from"
            min={today}
            className="w-full rounded-ele border border-ele-field-border bg-ele-field px-2 py-1 text-sm text-ele-strong"
            value={effectiveFrom}
            onChange={(e) => {
              setEffectiveFrom(e.target.value);
              setPreview(null);
            }}
          />
        </label>
        <label className="text-sm text-ele-body">
          Source note
          <input
            type="text"
            aria-label="Source note"
            maxLength={500}
            className="w-full rounded-ele border border-ele-field-border bg-ele-field px-2 py-1 text-sm text-ele-strong"
            value={sourceNote}
            placeholder="Why the figures changed"
            onChange={(e) => {
              setSourceNote(e.target.value);
              setPreview(null);
            }}
          />
        </label>
      </div>
      <RateFiguresFields form={form} onChange={change} />

      {error && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger px-3 py-2 text-sm text-ele-strong">
          {error}
        </p>
      )}
      {preview && (
        <div role="status" className="mt-3 rounded-ele border border-ele-purple-accent px-3 py-2 text-sm">
          <p className="font-menu text-xs uppercase tracking-widest text-ele-blue">
            {preview.label}, from {preview.effectiveFromLabel}
          </p>
          <p className="mt-1 text-ele-strong">{preview.summary}</p>
          {preview.changes.length > 0 && (
            <ul className="mt-1 space-y-1">
              {preview.changes.map((c, i) => (
                <li key={i} className="text-ele-body">
                  <span className="text-ele-strong">{c.what}</span>: {c.before} to {c.after}
                </li>
              ))}
            </ul>
          )}
          {preview.blocked.length > 0 && (
            <p className="mt-2 text-ele-strong">
              Not possible on this date: {preview.blocked.map((b) => `${b.company} (${b.reason})`).join("; ")}. Choose another effective date.
            </p>
          )}
          {needsAck && (
            <div className="mt-2">
              <p className="text-ele-strong">
                This would reprice {weeks.length} already approved week{weeks.length === 1 ? "" : "s"}:{" "}
                {weeks.map((w) => `${w.company}, ${w.engineerName}, week beginning ${formatDateLong(w.weekStarting)}`).join("; ")}.
              </p>
              <label className="mt-1 flex items-center gap-2 text-ele-body">
                <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />
                Yes, reprice those approved weeks. The change is recorded against my name.
              </label>
            </div>
          )}
        </div>
      )}
      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        {preview ? (
          <Button onClick={() => void save()} disabled={busy || !canSave}>
            {busy ? "Saving..." : `Save ${preview.label}`}
          </Button>
        ) : (
          <Button onClick={() => void review()} disabled={busy}>
            Review changes
          </Button>
        )}
      </div>
    </ModalShell>
  );
}
